import { useState, useEffect } from "react";
import { useSession } from "@/lib/auth-client";
import { apiFetch, ApiError } from "@/lib/api-client";

interface TenantMembership {
  tenantId: string;
  tenantName: string;
  roles: string[];
  permissions: string[];
  isDefault: boolean;
}

interface UserSession {
  user: {
    id: string;
    email: string;
    name: string;
  };
  tenants: TenantMembership[];
  session: {
    expiresAt: string;
  };
}

interface UseUserSessionResult {
  session: UserSession | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook that fetches the extended user session with tenant and permission info.
 *
 * This extends better-auth's useSession by calling our custom /auth/me endpoint
 * which includes:
 * - User's tenant memberships
 * - Roles per tenant
 * - Permissions per tenant (union of all role permissions)
 */
export function useUserSession(): UseUserSessionResult {
  const { data: betterAuthSession, isPending: isSessionPending } = useSession();
  const [session, setSession] = useState<UserSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    // Don't fetch if better-auth session is still loading
    if (isSessionPending) {
      return;
    }

    // If not authenticated, clear session
    if (!betterAuthSession) {
      setSession(null);
      setIsLoading(false);
      return;
    }

    // Fetch extended session with tenants and permissions
    const fetchSession = async () => {
      // Only show loading spinner on initial load, not on background refetches.
      // This prevents the entire page tree from unmounting when better-auth
      // re-validates the session on browser tab focus.
      if (!session) {
        setIsLoading(true);
      }
      setError(null);

      try {
        // Auth endpoints don't require tenant header
        const data = await apiFetch.get<UserSession>("/api/auth/me", {
          skipTenantHeader: true,
        });
        setSession(data);
      } catch (err) {
        if (err instanceof ApiError && err.isUnauthorized()) {
          setSession(null);
          return;
        }
        setError(err instanceof Error ? err : new Error("Unknown error"));
        setSession(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();
  }, [betterAuthSession, isSessionPending, fetchKey]);

  const refetch = () => setFetchKey((k) => k + 1);

  return {
    session,
    isLoading: isSessionPending || isLoading,
    error,
    refetch,
  };
}
